import { ccfRoutes } from '@app/ccf/ccf-routes';
import { weightOptions } from '@app/ccf/options/weight-options';
import { WeightOptionEnum } from '@shared/api/types';
import InputFormField from '@shared/components/form/input-form-field';
import SelectFormField from '@shared/components/form/select-form-field';
import GuideEditTemplate from '@shared/components/guide-edit-template';
import { FormLabel } from '@shared/components/ui/form';
import { useFormContext } from 'react-hook-form';
import {
  generatePath,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import SupplyFlowForm from '../components/supply-flow-form';
import { SupplyFlowContext, SupplyFlowFormValues } from '../supply-flow-layout';

export default function SupplyFlowEditPage() {
  const { trigger, watch, getValues } = useFormContext<SupplyFlowFormValues>();
  const navigate = useNavigate();
  const { report_uuid } = useParams();

  const context = useOutletContext<SupplyFlowContext>();
  const { onSubmit, isCreating, onDelete, isDeleting } = context;

  const selectedWeightOption = watch('weightOption');

  const onUpdateFlow = async () => {
    const isValid = await trigger();
    if (isValid) {
      const formData = getValues();
      await onSubmit(formData);
    }
  };

  const onCancel = () => {
    navigate(
      generatePath(ccfRoutes.SUPPLY_CHAIN_LOGISTICS, {
        report_uuid: report_uuid,
      })
    );
  };

  const isLoading = isCreating || isDeleting;

  return (
    <GuideEditTemplate
      preTitle="Edit supply flow"
      primaryActionText="Update flow"
      primaryActionProps={{
        onClick: onUpdateFlow,
        type: 'submit',
        loading: isLoading,
        disabled: isLoading,
      }}
      secondaryActionText="Cancel"
      secondaryActionProps={{
        onClick: onCancel,
        disabled: isLoading,
      }}
      destructiveActionText="Delete flow"
      destructiveActionProps={{
        onClick: onDelete,
        disabled: isLoading,
      }}
    >
      <form className="w-[700px]">
        <fieldset
          disabled={isLoading}
          className="flex w-full flex-col gap-6 text-left"
        >
          {/* Editable Name Field */}
          <div className="w-[350px] pr-4">
            <FormLabel className="text-left">Name</FormLabel>
            <InputFormField
              name="name"
              className="mt-2"
              placeholder="Enter flow name"
              data-1p-ignore
            />
          </div>

          {/* Weight and Cold Chain Section */}
          <div className="flex w-[700px] gap-8">
            {/* Weight Section */}
            <div className="flex-1">
              <FormLabel className="text-left">
                Weight of goods transported
              </FormLabel>
              <div className="mt-2 flex items-center gap-2">
                <InputFormField
                  name="weight"
                  type="number"
                  min={1}
                  max={
                    selectedWeightOption !== WeightOptionEnum['total_weight']
                      ? 100
                      : undefined
                  }
                  step={0.1}
                  className="w-32"
                />
                <SelectFormField
                  name="weightOption"
                  options={weightOptions}
                  placeholder="Select unit"
                  className="w-full"
                />
              </div>
            </div>

            {/* Cold Chain Percentage Section */}
            <div className="flex-1">
              <FormLabel className="text-left">Percentage cold-chain</FormLabel>
              <InputFormField
                name="coldChainPercentage"
                type="number"
                min={1}
                max={100}
                step={0.1}
                className="mt-2 w-24"
                adornmentRight="%"
              />
            </div>
          </div>

          <div className="space-y-2">
            <FormLabel className="text-left">Flow of goods</FormLabel>
            <SupplyFlowForm />
          </div>
        </fieldset>
      </form>
    </GuideEditTemplate>
  );
}
